import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ChevronsUpDown, Flag, ShieldCheck,
  EyeOff, Eye, Trash2, AlertTriangle, CheckCircle2, Loader2, ExternalLink, StickyNote, Send, Clock, RotateCcw,
} from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Loading } from "@/components/common/Loading";
import { StatusBadge } from "@/components/common/StatusBadge";
import { RichTextView } from "@/components/forms/RichTextEditor";
import { timeAgo } from "@/lib/timeago";
import { useRealtime } from "@/context/RealtimeContext";
import { AdminRefreshButton } from "@/components/admin/AdminRefreshButton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const STATUS_FILTERS = [
  { value: "all", label: "All reports" },
  { value: "pending", label: "Pending" },
  { value: "in_review", label: "In review" },
  { value: "closed", label: "Closed" },
  // Phase 7 §3: ordinary Group-directed reports don't enter the queue automatically —
  // this deliberate filter is how authorized staff reach them.
  { value: "group_only", label: "Group-directed" },
  // Everything that reached the platform tier by escalation (auto, reporter or organizer).
  { value: "escalated", label: "Escalated" },
];

// Moderation tabs — one per content family. Drives the `content_group` query param.
const GROUP_TABS = [
  { value: "chatter", label: "Chatter" },
  { value: "personal", label: "Personal" },
  { value: "business", label: "Business" },
  { value: "capital", label: "Capital" },
  { value: "event", label: "Events" },
  { value: "resource", label: "Resources" },
  { value: "group", label: "Groups" },
  { value: "group_message", label: "Private group messages" },
];

// Semantic tone per report lifecycle status (shared StatusBadge).
const REPORT_STATUS_TONE = { pending: "warning", in_review: "info", closed: "neutral", group_only: "neutral" };

// Why a report reached the platform tier (Phase 7 §4/§8).
const ESCALATION_LABEL = {
  safety_reason: "Auto-escalated — safety reason",
  organizer_conflict: "Auto-escalated — involves a group organizer",
  reporter_requested_platform_review: "Reporter requested Startup Otter review",
  group_organizer_referral: "Referred by a group organizer",
};

// Human label for the action taken on a report (shown in the table + history).
const ACTION_LABELS = {
  dismiss: "Dismissed",
  reviewed: "Reviewed",
  hide_content: "Hidden",
  unhide_content: "Unhidden",
  soft_delete_content: "Soft deleted",
  remove_content: "Removed",
  purge_content: "Permanently deleted",
  reopen: "Reopened",
};

const CONTENT_STATUS_TONE = {
  active: "success", hidden: "warning", soft_deleted: "danger", removed_by_moderation: "danger",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

const NOTE_KIND = {
  internal: { label: "Internal", tone: "neutral" },
  to_author: { label: "Sent to author", tone: "info" },
};

// Sortable column header — toggles A→Z / Z→A, shows the active direction.
const SORTABLE = [
  { field: "reason", label: "Reason" },
  { field: "reported", label: "Reported" },
  { field: "reporter", label: "Reporter" },
  { field: "status", label: "Status" },
  { field: "action", label: "Action taken" },
  { field: "date", label: "Date reported" },
];

function SortHead({ field, label, sort, order, onSort, className }) {
  const active = sort === field;
  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => onSort(field)}
        className={`inline-flex items-center gap-1 transition-colors hover:text-foreground ${active ? "text-foreground" : ""}`}
        data-testid={`moderation-sort-${field}`}
        aria-label={`Sort by ${label}${active ? (order === "asc" ? ", ascending" : ", descending") : ""}`}
      >
        {label}
        {active
          ? (order === "asc" ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />)
          : <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" />}
      </button>
    </TableHead>
  );
}

// Phase 7: `removed_by_group` is a Group organizer's own removal — shown as context for
// platform staff, who retain independent authority and override.
const INACTIVE = ["hidden", "soft_deleted", "removed_by_moderation", "removed_by_group"];

function ReviewDialog({ report, onClose, onUpdated }) {
  const [rep, setRep] = useState(report);
  const [internalNote, setInternalNote] = useState("");
  const [authorMsg, setAuthorMsg] = useState("");
  const [busy, setBusy] = useState(null);
  if (!rep) return null;

  const runAction = async (action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(action);
    try {
      const { data } = await api.post(`/admin/moderation/reports/${rep.id}/action`, { action });
      setRep(data);
      onUpdated(data);
      toast.success("Done");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(null); }
  };

  const addNote = async (kind) => {
    const text = (kind === "internal" ? internalNote : authorMsg).trim();
    if (!text) return;
    setBusy(`note-${kind}`);
    try {
      const { data } = await api.post(`/admin/moderation/reports/${rep.id}/notes`, { text, kind });
      setRep(data);
      onUpdated(data);
      if (kind === "internal") setInternalNote(""); else setAuthorMsg("");
      toast.success(kind === "internal" ? "Internal note added" : "Message sent to author");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add note");
    } finally { setBusy(null); }
  };

  const preview = rep.content_preview;
  const identity = rep.reported_identity || {};
  const reporter = rep.reporter || {};
  const status = rep.content_status || (preview && preview.status) || "active";
  const isInactive = INACTIVE.includes(status);
  const notes = rep.notes || [];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="moderation-review-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flag className="h-4 w-4 text-destructive" /> Review report
          </DialogTitle>
          <DialogDescription>{rep.reason_label}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-xs font-[560] uppercase tracking-wide text-muted-foreground">Reported {rep.content_type}</div>
              <div className="mt-0.5 font-[560] text-foreground">{identity.display_name || "—"}</div>
            </div>
            <div>
              <div className="text-xs font-[560] uppercase tracking-wide text-muted-foreground">Reported by</div>
              <div className="mt-0.5 font-[560] text-foreground">{reporter.display_name || "—"}</div>
            </div>
            <div>
              <div className="text-xs font-[560] uppercase tracking-wide text-muted-foreground">Date reported</div>
              <div className="mt-0.5 text-foreground" data-testid="moderation-reported-on">{fmtDate(rep.created_at)}</div>
            </div>
            <div>
              <div className="text-xs font-[560] uppercase tracking-wide text-muted-foreground">Date posted</div>
              <div className="mt-0.5 text-foreground" data-testid="moderation-posted-on">{fmtDate(rep.content_created_at)}</div>
            </div>
          </div>

          {rep.destination || rep.group_id ? (
            <div className="rounded-lg border border-border px-3 py-2 text-sm" data-testid="moderation-routing">
              <span className="text-xs font-[560] uppercase tracking-wide text-muted-foreground">Routing</span>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <StatusBadge tone={rep.destination === "group" ? "neutral" : "info"}>
                  {rep.destination === "group" ? "Reported to group organizers" : "Reported to Startup Otter"}
                </StatusBadge>
                {rep.group_review ? (
                  <StatusBadge tone="neutral" data-testid="moderation-group-review">
                    Group review: {rep.group_review.status || "pending"}
                    {rep.group_review.action ? ` · ${rep.group_review.action}` : ""}
                  </StatusBadge>
                ) : null}
              </div>
              {(rep.escalations || []).length ? (
                <ul className="mt-1.5 space-y-0.5" data-testid="moderation-escalations">
                  {rep.escalations.map((e) => (
                    <li key={e.id} className="text-xs text-muted-foreground">
                      {ESCALATION_LABEL[e.reason_code] || e.reason_code} · {fmtDate(e.at)}
                      {e.note ? ` — ${e.note}` : ""}
                    </li>
                  ))}
                </ul>
              ) : null}
              <p className="mt-1.5 text-xs text-muted-foreground">
                Group review state is read-only here — a platform decision never erases it.
              </p>
            </div>
          ) : null}

          {rep.details && (
            <div className="rounded-lg bg-secondary/60 px-3 py-2 text-sm text-foreground" data-testid="moderation-report-details">
              <span className="text-xs font-[560] uppercase tracking-wide text-muted-foreground">Reporter note</span>
              <p className="mt-1 break-words">{rep.details}</p>
            </div>
          )}

          <div className="rounded-lg border border-border p-3">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-xs font-[560] uppercase tracking-wide text-muted-foreground">Content preview</span>
              <StatusBadge tone={CONTENT_STATUS_TONE[status] || "neutral"}>{status.replace(/_/g, " ")}</StatusBadge>
            </div>
            {!rep.content_exists ? (
              <p className="text-sm italic text-muted-foreground">This content no longer exists.</p>
            ) : rep.is_entity ? (
              <div data-testid="moderation-content-preview" className="flex items-start gap-3">
                {preview?.image_url && <img src={fileUrl(preview.image_url)} alt="" className="h-14 w-14 shrink-0 rounded-md border border-border object-cover" />}
                <div className="min-w-0 flex-1">
                  <div className="break-words font-[600] text-foreground">{preview?.title || "—"}</div>
                  {preview?.subtitle && <div className="break-words text-sm text-muted-foreground">{preview.subtitle}</div>}
                  {rep.content_url && (
                    <a href={rep.content_url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-sm text-primary hover:underline" data-testid="moderation-view-live">
                      <ExternalLink className="h-3.5 w-3.5" /> View live
                    </a>
                  )}
                </div>
              </div>
            ) : (
              <div data-testid="moderation-content-preview">
                {preview.body && preview.body !== "<p></p>"
                  ? <RichTextView html={preview.body} />
                  : <p className="text-sm italic text-muted-foreground">No text.</p>}
                {preview.media && preview.media.image_url && (
                  <img src={fileUrl(preview.media.image_url)} alt="" className="mt-2 max-h-40 rounded-md border border-border object-cover" />
                )}
              </div>
            )}
          </div>

          {/* Notes thread */}
          <div className="space-y-2">
            <div className="text-xs font-[560] uppercase tracking-wide text-muted-foreground">Moderation notes</div>
            {notes.length === 0 ? (
              <p className="text-sm italic text-muted-foreground" data-testid="moderation-notes-empty">No notes yet.</p>
            ) : (
              <div className="space-y-2" data-testid="moderation-notes-list">
                {notes.map((n) => (
                  <div key={n.id} className="rounded-lg bg-secondary/50 px-3 py-2" data-testid={`moderation-note-${n.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-[560] text-foreground">{n.author?.display_name || "Moderator"}</span>
                        <StatusBadge tone={(NOTE_KIND[n.kind] || NOTE_KIND.internal).tone}>{(NOTE_KIND[n.kind] || NOTE_KIND.internal).label}</StatusBadge>
                      </div>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground"><Clock className="h-3 w-3" /> {fmtDate(n.created_at)}</span>
                    </div>
                    <p className="mt-1 break-words text-sm text-foreground">{n.text}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="rounded-lg border border-border p-2">
              <Textarea value={internalNote} onChange={(e) => setInternalNote(e.target.value)} rows={2} placeholder="Internal note (moderators only)…" data-testid="moderation-internal-note" />
              <div className="mt-2 flex justify-end">
                <Button size="sm" variant="secondary" disabled={!internalNote.trim() || !!busy} onClick={() => addNote("internal")} data-testid="moderation-add-internal-note">
                  {busy === "note-internal" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <StickyNote className="mr-2 h-4 w-4" />} Add internal note
                </Button>
              </div>
            </div>

            <div className="rounded-lg border border-border p-2">
              <Textarea value={authorMsg} onChange={(e) => setAuthorMsg(e.target.value)} rows={2} placeholder="Message to the author (delivered privately)…" data-testid="moderation-author-message" />
              <div className="mt-2 flex justify-end">
                <Button size="sm" variant="secondary" disabled={!authorMsg.trim() || !rep.reported_actor_user_id || !!busy} onClick={() => addNote("to_author")} data-testid="moderation-send-author" title={!rep.reported_actor_user_id ? "No known author to message" : undefined}>
                  {busy === "note-to_author" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Send to author
                </Button>
              </div>
            </div>
          </div>

          {/* Status actions */}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {rep.status === "closed" && (
              <Button size="sm" variant="default" disabled={!!busy} onClick={() => runAction("reopen")} className="justify-start sm:col-span-2" data-testid="moderation-action-reopen">
                {busy === "reopen" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />} Reopen case
              </Button>
            )}
            <Button size="sm" variant="secondary" disabled={!!busy} onClick={() => runAction("dismiss")} className="justify-start" data-testid="moderation-action-dismiss">
              {busy === "dismiss" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />} Dismiss report
            </Button>
            <Button size="sm" variant="secondary" disabled={!!busy} onClick={() => runAction("reviewed")} className="justify-start" data-testid="moderation-action-reviewed">
              {busy === "reviewed" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />} Mark reviewed
            </Button>
            {rep.content_exists && !isInactive && (
              <Button size="sm" variant="outline" disabled={!!busy} onClick={() => runAction("hide_content")} className="justify-start" data-testid="moderation-action-hide_content">
                {busy === "hide_content" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <EyeOff className="mr-2 h-4 w-4" />} Hide content
              </Button>
            )}
            {rep.content_exists && isInactive && (
              <Button size="sm" variant="outline" disabled={!!busy} onClick={() => runAction("unhide_content")} className="justify-start" data-testid="moderation-action-unhide_content">
                {busy === "unhide_content" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Eye className="mr-2 h-4 w-4" />} Unhide / restore
              </Button>
            )}
            {rep.content_exists && (
              <Button size="sm" variant="outline" disabled={!!busy} onClick={() => runAction("soft_delete_content")} className="justify-start" data-testid="moderation-action-soft_delete_content">
                {busy === "soft_delete_content" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />} {rep.is_entity ? "Delete (soft)" : "Soft-delete"}
              </Button>
            )}
            {!rep.is_entity && rep.content_exists && (
              <Button size="sm" variant="destructive" disabled={!!busy} onClick={() => runAction("remove_content")} className="justify-start" data-testid="moderation-action-remove_content">
                {busy === "remove_content" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <AlertTriangle className="mr-2 h-4 w-4" />} Remove (moderation)
              </Button>
            )}
            {rep.is_entity && rep.content_exists && status === "soft_deleted" && (
              <Button size="sm" variant="destructive" disabled={!!busy} onClick={() => runAction("purge_content", "Permanently delete this content? This cannot be undone.")} className="justify-start sm:col-span-2" data-testid="moderation-action-purge_content">
                {busy === "purge_content" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <AlertTriangle className="mr-2 h-4 w-4" />} Permanently delete
              </Button>
            )}
          </div>

          {/* Action history */}
          <div className="space-y-1.5 border-t border-border pt-3">
            <div className="text-xs font-[560] uppercase tracking-wide text-muted-foreground">Action history</div>
            {(rep.action_history || []).length === 0 ? (
              <p className="text-sm italic text-muted-foreground" data-testid="moderation-history-empty">No actions taken yet.</p>
            ) : (
              <ul className="space-y-1" data-testid="moderation-history-list">
                {rep.action_history.map((h) => (
                  <li key={h.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`moderation-history-${h.id}`}>
                    <span className="text-foreground">
                      <span className="font-[560]">{ACTION_LABELS[h.action] || h.action}</span>
                      <span className="text-muted-foreground"> by {h.by?.display_name || "Moderator"}</span>
                    </span>
                    <span className="flex items-center gap-1 whitespace-nowrap text-xs text-muted-foreground"><Clock className="h-3 w-3" /> {fmtDate(h.at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminModeration() {
  const [status, setStatus] = useState("all");
  const [group, setGroup] = useState("chatter");
  const [reason, setReason] = useState("all");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("date");
  const [order, setOrder] = useState("desc");
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null); // report under review
  const [reloadKey, setReloadKey] = useState(0); // bump to refetch (e.g. after an action)
  const { markSectionRead } = useRealtime();

  // Click a column header: same field toggles A↔Z, a new field starts at A→Z (date starts newest-first).
  const onSort = (field) => {
    setPage(1);
    if (sort === field) {
      setOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSort(field);
      setOrder(field === "date" ? "desc" : "asc");
    }
  };

  // Clear the "moderation" sidebar badge once a moderator opens the queue.
  useEffect(() => {
    const t = setTimeout(() => markSectionRead && markSectionRead("moderation"), 0);
    return () => clearTimeout(t);
  }, [markSectionRead]);

  // Fetch reports. Setter lives inside the async IIFE so it never runs synchronously
  // during the effect (keeps the build's `set-state-in-effect` rule happy).
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const params = { status, page, page_size: 20, sort, order, content_group: group };
        if (reason !== "all") params.reason = reason;
        const { data } = await api.get("/admin/moderation/reports", { params });
        if (active) setData(data);
      } catch { if (active) setData({ items: [], total: 0, reasons: [] }); }
    })();
    return () => { active = false; };
  }, [status, group, reason, page, sort, order, reloadKey]);

  const reasons = data?.reasons || [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / 20)) : 1;

  const onUpdated = (updated) => {
    // Keep the list row in sync after an action/note (dialog stays open so history is visible).
    setData((prev) => prev && { ...prev, items: prev.items.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)) });
    setReloadKey((k) => k + 1);
  };

  const reopenReport = async (r) => {
    try {
      const { data } = await api.post(`/admin/moderation/reports/${r.id}/action`, { action: "reopen" });
      onUpdated(data);
      toast.success("Reopened");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reopen");
    }
  };

  return (
    <div data-testid="admin-moderation">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-lg font-[640] text-foreground">Moderation</h2>
        <AdminRefreshButton
          testId="admin-refresh-moderation"
          onRefresh={() => { setReloadKey((k) => k + 1); }}
        />
      </div>
      <Tabs value={group} onValueChange={(v) => { setGroup(v); setPage(1); setReason("all"); }} className="mb-4">
        <TabsList data-testid="moderation-group-tabs" className="flex-wrap">
          {GROUP_TABS.map((g) => (
            <TabsTrigger key={g.value} value={g.value} data-testid={`moderation-tab-${g.value}`}>{g.label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground" data-testid="moderation-summary">
          <Flag className="h-4 w-4 text-primary" />
          <span data-testid="moderation-open-count">
            <strong className="text-foreground">{data?.open ?? 0}</strong> open case{(data?.open ?? 0) === 1 ? "" : "s"}
          </span>
          <span className="text-border">·</span>
          <span data-testid="moderation-pending-count">{data?.pending ?? 0} pending</span>
          <span className="text-border">·</span>
          <span data-testid="moderation-inreview-count">{data?.in_review ?? 0} in review</span>
          <span className="text-border">·</span>
          <span data-testid="moderation-grouponly-count">{data?.group_only ?? 0} group-directed</span>
          <span className="text-border">·</span>
          <span data-testid="moderation-escalated-count">{data?.escalated ?? 0} escalated</span>
        </div>
        <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:justify-end">
          <Select value={status} onValueChange={(v) => { setPage(1); setStatus(v); }}>
            <SelectTrigger className="w-full sm:w-40" data-testid="moderation-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUS_FILTERS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={reason} onValueChange={(v) => { setPage(1); setReason(v); }}>
            <SelectTrigger className="w-full sm:w-44" data-testid="moderation-filter-reason"><SelectValue placeholder="All reasons" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All reasons</SelectItem>
              {reasons.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="mt-4 border-0 p-0">
        {!data ? <div className="p-6"><Loading /></div> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {SORTABLE.map((c) => (
                    <SortHead key={c.field} field={c.field} label={c.label} sort={sort} order={order} onSort={onSort} />
                  ))}
                  <TableHead className="text-right">Review</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((r) => (
                  <TableRow key={r.id} data-testid={`moderation-report-${r.id}`}>
                    <TableCell>
                      <div className="font-[540] text-foreground">{r.reason_label}</div>
                      <div className="text-xs capitalize text-muted-foreground">{r.content_type}{!r.content_exists ? " · deleted" : ""}</div>
                      {/* Phase 7 — the tabs are per content type, so a Group post lives under
                          Chatter. The Group and the escalation are surfaced on the row so a
                          moderator can see both without opening the report. */}
                      {r.group_name || r.escalated ? (
                        <div className="mt-1 flex flex-wrap items-center gap-1" data-testid={`moderation-row-routing-${r.id}`}>
                          {r.group_name ? (
                            <StatusBadge tone="neutral" data-testid={`moderation-row-group-${r.id}`}>
                              Group: {r.group_name}
                            </StatusBadge>
                          ) : null}
                          {r.escalated ? (
                            <StatusBadge tone="warning" data-testid={`moderation-row-escalated-${r.id}`}>
                              Escalated
                            </StatusBadge>
                          ) : null}
                          {r.destination === "group" ? (
                            <StatusBadge tone="neutral">To organizers</StatusBadge>
                          ) : null}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell><div className="font-[540] text-foreground">{r.reported_identity?.display_name || "—"}</div></TableCell>
                    <TableCell><div className="text-sm text-foreground">{r.reporter?.display_name || "—"}</div></TableCell>
                    <TableCell><StatusBadge tone={REPORT_STATUS_TONE[r.status] || "neutral"}>{r.status.replace(/_/g, " ")}</StatusBadge></TableCell>
                    <TableCell>
                      {r.moderation_action
                        ? <span className="text-sm font-[540] text-foreground" data-testid={`moderation-action-taken-${r.id}`}>{ACTION_LABELS[r.moderation_action] || r.moderation_action}</span>
                        : <span className="text-sm text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell><span className="whitespace-nowrap text-sm text-muted-foreground" title={timeAgo(r.created_at)}>{fmtDate(r.created_at)}</span></TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {r.status === "closed" && (
                          <Button size="sm" variant="ghost" onClick={() => reopenReport(r)} data-testid={`moderation-reopen-${r.id}`} title="Reopen case">
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => setActive(r)} data-testid={`moderation-review-${r.id}`} title="Review">
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {data.items.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground">No reports match these filters.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <div className="mt-3 flex items-center justify-between">
        <div className="text-sm text-muted-foreground">{data?.total ?? 0} reports</div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="moderation-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-sm text-muted-foreground">Page {page} / {totalPages}</span>
          <Button size="sm" variant="secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="moderation-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <ReviewDialog key={active?.id || "none"} report={active} onClose={() => setActive(null)} onUpdated={onUpdated} />
    </div>
  );
}
